import { fetchApi } from "@/config/api";
import type {
  Produit,
  ProductsPageResult,
  FilterProduitInput,
  ProduitCatalogue,
} from "@/types/produit";
import {
  ProduitDescription,
  SaveProduitDescriptionPayload,
} from "@/types/produitDescription";
import { CompositionOption } from "@/types/compositionOption";
import { CompositionFamilleOption } from "@/types/compositionFamilleOption";
import { ProduitStock } from "@/types/produitStock";
import { ProduitCompositionItem } from "@/types/produitCompositionItem";
import { CaracteristiqueOption } from "@/types/caracteristiqueOption";
import { PackOption } from "@/types/packOption";
import { VendeurCouleur } from "@/types/vendeur-couleur";

// Re-export des types pour la rétrocompatibilité
export type {
  Produit,
  ProductsPageResult,
  FilterProduitInput,
  ProduitCatalogue,
};

export interface ProvenanceOption {
  id_provenance: number;
  label: string;
}

type UpdateProduitPayload = {
  id_produit: number;
  reference?: string;
  reference_base?: string;
  prix?: number;
  prixReduit?: number | null;
  id_collection?: number | null;
  id_categorie?: number | null;
  visible?: boolean;
  poids?: number;
  vendu_par?: string;
  id_vendeur_marque?: number | null;
  id_pack?: number | null;
  id_declinaison?: number | null;
  id_provenance?: number | null;
  dimension?: string | null;
  id_couleur_liee?: number | null;
  main?: boolean;
};

type UpdateProduitPromotionPayload = {
  id_produit: number;
  id_vendeur: number;
  promotion?: number | null;
};

type UpdateProduitCouleursPayload = {
  id_produit: number;
  ids_couleur_efashion: number[];
  couleurs_with_quantities?: Array<{
    id_couleur: number;
    description_paquet?: string | null;
  }>;
  vendu_par?: string | null;
};

export interface ProduitLinkPreviewItem {
  id_produit: number;
  id_vendeur?: number | null;
  reference?: string | null;
  reference_base?: string | null;
  next_reference_base?: string | null;
  next_reference?: string | null;
  id_couleur_liee?: number | null;
  main: boolean;
  premel?: string | null;
  visible: boolean;
  supprimer: boolean;
  id_couleur?: number | null;
  couleur?: string | null;
  vendu_par?: string | null;
  nb_photos?: number | null;
  poids?: number | null;
  selected: boolean;
  willBeMain: boolean;
  willBeDetached: boolean;
  duplicateColorKey?: string | null;
  currentGroupId?: number | null;
  nextGroupId?: number | null;
}

export interface ProduitLinkPreviewGroup {
  groupId: number;
  products: ProduitLinkPreviewItem[];
}

export interface ProduitLinkDuplicateColorGroup {
  colorKey: string;
  id_couleur?: number | null;
  couleur?: string | null;
  keepProductId?: number | null;
  products: ProduitLinkPreviewItem[];
}

export interface ProduitLinkReferenceBaseOption {
  referenceBase: string;
  products: ProduitLinkPreviewItem[];
}

export interface ProduitLinkPreview {
  canApply: boolean;
  warnings: string[];
  blockingErrors: string[];
  affectedProductIds: number[];
  groupsBefore: ProduitLinkPreviewGroup[];
  groupAfter?: ProduitLinkPreviewGroup | null;
  duplicateColorGroups: ProduitLinkDuplicateColorGroup[];
  referenceBaseOptions: ProduitLinkReferenceBaseOption[];
  targetReferenceBase?: string | null;
  confirmationToken?: string | null;
}

/**
 * Récupérer les produits avec pagination et filtres
 */
export async function getProductsPage(
  filter: FilterProduitInput,
): Promise<ProductsPageResult> {
  // Safety net: backend requires id_vendeur; inject it from auth store if caller omitted it
  try {
    // Lazy import to avoid circular deps
    const { useAuthStore } = await import("@/stores/auth");
    const auth = useAuthStore();
    if ((filter as any).id_vendeur == null) {
      // Ensure auth is initialized before reading user
      if (!auth.isInitialized) {
        try {
          await auth.checkAuth();
        } catch (_) {
          /* ignore */
        }
      }
      const idV = (auth.user as any)?.id_vendeur ?? auth.user?.id;
      if (idV != null && !isNaN(Number(idV)) && Number(idV) > 0) {
        (filter as any).id_vendeur = Number(idV);
      }
    }
  } catch (_) {
    // ignore — we'll rely on whatever the caller provided
  }
  const query = `
    query GetProductsPage($filter: FilterProduitInput!) {
      productsPage(filter: $filter) {
        items {
          id_produit
          id_vendeur
          date_produit
          date_modification
          reference
          reference_base
          marque
          id_vendeur_marque
          collection
          id_collection
          categorie
          id_categorie 
          prix
          prixReduit   
          promotion
          poids
          dimension
          visible
          supprimer
          id_couleur
          couleur
          couleursDetail {
            id_couleur
            couleur_FR
            hexColor
          }
          stock_value
          stock_renseigne
          liaison
          vendu_par
          id_pack
          id_declinaison
          id_provenance
          provenance
          premel
          nb_photos
          id_shooting
          finalisation
          prestationValeur
          dateFinalisation
          id_couleur_liee
          main
        }
        total
      }
    }
  `;
  const response = await fetchApi<{ productsPage: ProductsPageResult }>(query, {
    filter,
  });
  return response.productsPage;
}

/**
 * Publier un produit brouillon (premel='1' → premel='0', visible=true)
 */
export async function publishBrouillon(
  idProduit: number,
  idVendeur: number,
): Promise<boolean> {
  const query = `
    mutation PublishBrouillon($id_produit: Int!, $id_vendeur: Int!) {
      publishBrouillon(id_produit: $id_produit, id_vendeur: $id_vendeur)
    }
  `;
  const response = await fetchApi<{ publishBrouillon: boolean }>(query, {
    id_produit: Number(idProduit),
    id_vendeur: Number(idVendeur),
  });
  return response.publishBrouillon;
}

/**
 * Publier plusieurs produits brouillon en lot
 */
export async function publishBrouillonBulk(
  idProduits: number[],
  idVendeur: number,
): Promise<number> {
  const query = `
    mutation PublishBrouillonBulk($id_produits: [Int!]!, $id_vendeur: Int!) {
      publishBrouillonBulk(id_produits: $id_produits, id_vendeur: $id_vendeur)
    }
  `;
  const response = await fetchApi<{ publishBrouillonBulk: number }>(query, {
    id_produits: idProduits.map(Number),
    id_vendeur: Number(idVendeur),
  });
  return response.publishBrouillonBulk;
}

/**
 * Rechercher des produits par référence ou ID (autocomplete)
 */
export async function searchProductsByReference(
  idVendeur: number,
  searchTerm: string,
  limit = 20,
): Promise<Produit[]> {
  // Vérifier si le terme de recherche est un nombre (ID) ou du texte (référence)
  const isNumeric = /^\d+$/.test(searchTerm.trim());

  const filter: FilterProduitInput = {
    id_vendeur: idVendeur,
    //supprimer: false,
    take: limit,
    skip: 0,
    orderBy: "reference",
    orderDir: "ASC",
  };

  // Si c'est un nombre, chercher par ID, sinon par référence
  if (isNumeric) {
    filter.id_produit_contains = searchTerm;
  } else {
    filter.reference_contains = searchTerm;
  }

  const result = await getProductsPage(filter);
  return result.items;
}

/**
 *  Récupérer le nombre total de produits d'un vendeur
 */
/**
 * Obtenir l'URL acheteur pour un produit spécifique
 */
export async function getProductAcheteurUrl(
  idProduit: number,
): Promise<string | null> {
  const query = `
    query GetProductAcheteurUrl($id_produit: Int!) {
      getProductAcheteurUrl(id_produit: $id_produit)
    }
  `;
  const response = await fetchApi<{ getProductAcheteurUrl: string | null }>(query, {
    id_produit: idProduit,
  });
  return response.getProductAcheteurUrl;
}

export async function getTotalProduitsByVendeur(
  idVendeur: number,
): Promise<number> {
  // Use dedicated lightweight resolver instead of productsPage to avoid heavy joins and long latency
  const query = `
      query ($id_vendeur: Int!) {
        totalProduitsVendeur(id_vendeur: $id_vendeur)
      }
    `;
  const res = await fetchApi<{ totalProduitsVendeur: number }>(query, {
    id_vendeur: idVendeur,
  });
  const total = res.totalProduitsVendeur;
  return typeof total === "number" && !Number.isNaN(total) ? total : 0;
}

export async function updateProduit(
  payload: UpdateProduitPayload,
): Promise<Produit> {
  const mutation = `
    mutation UpdateProduit($input: UpdateProduitInput!) {
      updateProduit(input: $input) {
        id_produit
        reference
        id_collection
        id_categorie
        prix
        poids
        vendu_par
        visible
        id_pack  
        id_couleur_liee
        main
      }
    }
  `;

  const resp = await fetchApi<{ updateProduit: Produit }>(mutation, {
    input: payload,
  });

  return resp.updateProduit;
}

const PRODUCT_LINK_PREVIEW_FIELDS = `
  canApply
  warnings
  blockingErrors
  affectedProductIds
  confirmationToken
  groupsBefore {
    groupId
    products {
      id_produit
      id_vendeur
      reference
      reference_base
      next_reference_base
      next_reference
      id_couleur_liee
      main
      premel
      visible
      supprimer
      id_couleur
      couleur
      vendu_par
      nb_photos
      poids
      selected
      willBeMain
      willBeDetached
      duplicateColorKey
      currentGroupId
      nextGroupId
    }
  }
  groupAfter {
    groupId
    products {
      id_produit
      id_vendeur
      reference
      reference_base
      next_reference_base
      next_reference
      id_couleur_liee
      main
      premel
      visible
      supprimer
      id_couleur
      couleur
      vendu_par
      nb_photos
      poids
      selected
      willBeMain
      willBeDetached
      duplicateColorKey
      currentGroupId
      nextGroupId
    }
  }
  duplicateColorGroups {
    colorKey
    id_couleur
    couleur
    keepProductId
    products {
      id_produit
      id_vendeur
      reference
      reference_base
      next_reference_base
      next_reference
      id_couleur_liee
      main
      premel
      visible
      supprimer
      id_couleur
      couleur
      vendu_par
      nb_photos
      poids
      selected
      willBeMain
      willBeDetached
      duplicateColorKey
      currentGroupId
      nextGroupId
    }
  }
  referenceBaseOptions {
    referenceBase
    products {
      id_produit
      id_vendeur
      reference
      reference_base
      next_reference_base
      next_reference
      id_couleur_liee
      main
      premel
      visible
      supprimer
      id_couleur
      couleur
      vendu_par
      nb_photos
      poids
      selected
      willBeMain
      willBeDetached
      duplicateColorKey
      currentGroupId
      nextGroupId
    }
  }
  targetReferenceBase
`;

export async function previewProduitLink(
  productIds: number[],
  mainProductId?: number | null,
  duplicateColorKeepProductIds: number[] = [],
  targetReferenceBase?: string | null,
): Promise<ProduitLinkPreview> {
  const mutation = `
    mutation PreviewProduitLink($input: PreviewProduitLinkInput!) {
      previewProduitLink(input: $input) {
        ${PRODUCT_LINK_PREVIEW_FIELDS}
      }
    }
  `;
  const response = await fetchApi<{ previewProduitLink: ProduitLinkPreview }>(
    mutation,
    {
      input: {
        productIds: productIds.map(Number),
        mainProductId: mainProductId != null ? Number(mainProductId) : null,
        duplicateColorKeepProductIds: duplicateColorKeepProductIds.map(Number),
        targetReferenceBase: targetReferenceBase?.trim() || null,
      },
    },
  );
  return response.previewProduitLink;
}

export async function applyProduitLink(
  productIds: number[],
  mainProductId: number,
  confirmationToken: string,
  duplicateColorKeepProductIds: number[] = [],
  targetReferenceBase?: string | null,
): Promise<ProduitLinkPreview> {
  const mutation = `
    mutation ApplyProduitLink($input: ApplyProduitLinkInput!) {
      applyProduitLink(input: $input) {
        ${PRODUCT_LINK_PREVIEW_FIELDS}
      }
    }
  `;
  const response = await fetchApi<{ applyProduitLink: ProduitLinkPreview }>(
    mutation,
    {
      input: {
        productIds: productIds.map(Number),
        mainProductId: Number(mainProductId),
        confirmationToken,
        duplicateColorKeepProductIds: duplicateColorKeepProductIds.map(Number),
        targetReferenceBase: targetReferenceBase?.trim() || null,
      },
    },
  );
  return response.applyProduitLink;
}

export async function updateProduitPromotion(
  payload: UpdateProduitPromotionPayload,
): Promise<boolean> {
  const mutation = `
    mutation UpdateProduitPromotion($input: UpdateProduitPromotionInput!) {
      updateProduitPromotion(input: $input)
    }
  `;

  const resp = await fetchApi<{ updateProduitPromotion: boolean }>(mutation, {
    input: payload,
  });

  return resp.updateProduitPromotion;
}

export async function updateProduitCouleursProduit(
  payload: UpdateProduitCouleursPayload,
): Promise<boolean> {
  const mutation = `
    mutation UpdateProduitCouleursProduit($input: UpdateProduitCouleursInput!) {
      updateProduitCouleursProduit(input: $input)
    }
  `;

  const resp = await fetchApi<{ updateProduitCouleursProduit: boolean }>(
    mutation,
    { input: payload },
  );

  return resp.updateProduitCouleursProduit;
}

// 更新产品颜色并同步更新reference
export async function updateProduitCouleur(
  idProduit: number,
  couleurId: number,
  couleurName: string,
): Promise<boolean> {
  const mutation = `
    mutation UpdateProduitCouleur($idProduit: Int!, $couleurId: Int!, $couleurName: String!) {
      updateProduitCouleur(idProduit: $idProduit, couleurId: $couleurId, couleurName: $couleurName)
    }
  `;

  const resp = await fetchApi<{ updateProduitCouleur: boolean }>(mutation, {
    idProduit,
    couleurId,
    couleurName,
  });

  return resp.updateProduitCouleur;
}

export async function duplicateProduit(id_produit: number): Promise<Produit> {
  const mutation = `
    mutation DuplicateProduit($id_produit: Int!) {
      duplicateProduit(id_produit: $id_produit) {
        id_produit
        id_vendeur
        dateCreation   
        reference
        reference_base
        id_vendeur_marque
        id_collection
        id_categorie
        prix
        prixReduit  
        poids
        visible
        supprimer
        vendu_par
        id_pack
        id_declinaison
        id_provenance
      }
    }
  `;

  const resp = await fetchApi<{ duplicateProduit: Produit }>(mutation, {
    id_produit,
  });

  return resp.duplicateProduit;
}

export async function duplicateProduitForMel(
  id_produit: number,
  newReference: string,
  id_vendeur?: number,
  existingShootingId?: number,
): Promise<Produit> {
  const mutation = `
    mutation DuplicateProduitForMel($id_produit: Int!, $newReference: String!, $id_vendeur: Int, $existingShootingId: Int) {
      duplicateProduitForMel(id_produit: $id_produit, newReference: $newReference, id_vendeur: $id_vendeur, existingShootingId: $existingShootingId) {
        id_produit
        id_vendeur
        dateCreation
        reference
        reference_base
        id_vendeur_marque
        id_collection
        id_categorie
        prix
        prixReduit
        poids
        visible
        supprimer
        vendu_par
        id_pack
        id_declinaison
        id_provenance
        id_shooting
        premel
        main
        id_couleur_liee
      }
    }
  `;

  const resp = await fetchApi<{ duplicateProduitForMel: Produit }>(mutation, {
    id_produit,
    newReference,
    id_vendeur,
    existingShootingId,
  });

  return resp.duplicateProduitForMel;
}

export async function applyGlobalPromotion(
  id_vendeur: number,
  pourcentage: number,
  force = false,
): Promise<boolean> {
  const mutation = `
    mutation ApplyGlobalPromotion($id_vendeur: Int!, $pourcentage: Float!, $force: Boolean) {
      applyGlobalPromotion(id_vendeur: $id_vendeur, pourcentage: $pourcentage, force: $force)
    }
  `;
  const resp = await fetchApi<{ applyGlobalPromotion: boolean }>(mutation, {
    id_vendeur,
    pourcentage,
    force,
  });
  return resp.applyGlobalPromotion;
}

export async function getCouleursByVendeur(
  id_vendeur: number,
): Promise<VendeurCouleur[]> {
  const query = `
    query CouleursByVendeur($id_vendeur: Int!) {
      couleursByVendeur(id_vendeur: $id_vendeur) {
        id_couleur_vendeur
        id_vendeur
        id_couleur
        couleur_FR
        couleur_EN
        hexColor
      }
    }
  `;

  const resp = await fetchApi<{ couleursByVendeur: VendeurCouleur[] }>(query, {
    id_vendeur,
  });

  return resp.couleursByVendeur;
}

export async function getPacksByVendeur(
  id_vendeur: number,
): Promise<PackOption[]> {
  const query = `
    query PacksByVendeur($id_vendeur: Int!) {
      packsByVendeur(id_vendeur: $id_vendeur) {
        id_pack
        id_vendeur
        titre
        p1
        p2
        p3
        p4
        p5
        p6
        p7
        p8
        p9
        p10
        p11
        p12
      }
    }
  `;

  const resp = await fetchApi<{ packsByVendeur: any[] }>(query, {
    id_vendeur,
  });

  return resp.packsByVendeur.map((p) => {
    const allQuantities = [p.p1, p.p2, p.p3, p.p4, p.p5, p.p6, p.p7, p.p8, p.p9, p.p10, p.p11, p.p12]
      .map((value) => Number(value ?? 0));
    const length = allQuantities.reduce(
      (lastIndex, quantity, index) => quantity > 0 ? index + 1 : lastIndex,
      0,
    );
    const quantities = allQuantities.slice(0, length);
    return {
      id_pack: p.id_pack,
      id_vendeur: p.id_vendeur,
      titre: p.titre,
      valeur: quantities.join(','),
    };
  });
}

export interface VendeurDeclinaison {
  id_declinaison: number;
  id_vendeur: number;
  titre: string;
}

export async function getDeclinaisonsByVendeur(
  id_vendeur: number,
): Promise<VendeurDeclinaison[]> {
  const query = /* GraphQL */ `
    query DeclinaisonsByVendeur($idVendeur: Int!) {
      declinaisonsByVendeur(idVendeur: $idVendeur) {
        id_declinaison
        id_vendeur
        titre
      }
    }
  `;

  const data = await fetchApi(query, {
    idVendeur: id_vendeur,
  });

  return data.declinaisonsByVendeur;
}

export async function getProvenances(lang = "fr"): Promise<ProvenanceOption[]> {
  const query = `
    query GetProvenances($lang: String) {
      provenances(lang: $lang) {
        id_provenance
        libelle
      }
    }
  `;

  const resp = await fetchApi<{ provenances: ProvenanceOption[] }>(query, {
    lang,
  });

  return resp.provenances;
}

export async function getCompositionFamilies(
  lang = "fr",
): Promise<CompositionFamilleOption[]> {
  const query = `
    query GetCompositionFamilies($lang: String) {
      compositionLocalisations(lang: $lang) {
        id_composition_localisation
        libelle
      }
    }
  `;

  const resp = await fetchApi<{
    compositionLocalisations: CompositionFamilleOption[];
  }>(query, { lang });

  return resp.compositionLocalisations;
}

export async function getProduitStocks(
  id_produit: number,
): Promise<ProduitStock[]> {
  const query = `
    query ProduitStocks($id_produit: Int!) {
      produitStocks(id_produit: $id_produit) {
        id_produit_stock
        id_produit
        id_couleur
        value
        taille
        couleur_FR
        hexColor
      }
    }
  `;

  const resp = await fetchApi<{ produitStocks: ProduitStock[] }>(query, {
    id_produit,
  });

  return resp.produitStocks;
}

export async function getCompositions(
  lang = "fr",
  search = "",
): Promise<CompositionOption[]> {
  const query = `
    query GetCompositions($lang: String, $search: String) {
      compositionsMaster(lang: $lang, search: $search) {
        id_composition
        libelle
      }
    }
  `;

  const resp = await fetchApi<{ compositionsMaster: CompositionOption[] }>(
    query,
    {
      lang,
      search,
    },
  );

  return resp.compositionsMaster;
}
export async function getProduitCompositions(
  id_produit: number,
  lang = "fr",
): Promise<ProduitCompositionItem[]> {
  const query = `
    query ProduitCompositions($id_produit: Int!, $lang: String) {
      produitCompositions(id_produit: $id_produit, lang: $lang) {
        id_composition
        id_composition_localisation
        value
        famille
        libelle
      }
    }
  `;

  const resp = await fetchApi<{
    produitCompositions: ProduitCompositionItem[];
  }>(query, { id_produit, lang });

  return resp.produitCompositions;
}

export async function saveProduitCompositions(
  id_produit: number,
  items: {
    id_composition: number;
    id_composition_localisation: number;
    value: number;
  }[],
): Promise<boolean> {
  const mutation = `
    mutation SaveProduitCompositions($input: SaveProduitCompositionsInput!) {
      saveProduitCompositions(input: $input)
    }
  `;

  const resp = await fetchApi<{ saveProduitCompositions: boolean }>(mutation, {
    input: {
      id_produit,
      items,
    },
  });

  return resp.saveProduitCompositions;
}
export async function getCaracteristiques(
  lang: "fr" | "en" = "fr",
  idCategorie?: number,
): Promise<CaracteristiqueOption[]> {
  const query = `
    query GetCaracteristiques($lang: String, $id_categorie: Int) {
      caracteristiques(lang: $lang, id_categorie: $id_categorie) {
        id_caracteristique
        libelle
      }
    }
  `;

  const variables: any = { lang };
  if (typeof idCategorie === "number") {
    variables.id_categorie = idCategorie;
  }

  const resp = await fetchApi<{ caracteristiques: CaracteristiqueOption[] }>(
    query,
    variables,
  );
  return resp.caracteristiques;
}

export async function getProduitCaracteristiqueIds(
  id_produit: number,
): Promise<number[]> {
  const query = `
    query GetProduitCaracteristiqueIds($id_produit: Int!) {
      produitCaracteristiqueIds(id_produit: $id_produit)
    }
  `;

  const resp = await fetchApi<{ produitCaracteristiqueIds: number[] }>(query, {
    id_produit,
  });
  return resp.produitCaracteristiqueIds;
}

export interface UpdateProduitCaracteristiquesPayload {
  id_produit: number;
  id_categorie?: number | null;
  ids_caracteristiques: number[];
}

export async function updateProduitCaracteristiques(
  payload: UpdateProduitCaracteristiquesPayload,
): Promise<boolean> {
  const mutation = `
    mutation UpdateProduitCaracteristiques($input: UpdateProduitCaracteristiquesInput!) {
      updateProduitCaracteristiques(input: $input)
    }
  `;

  const resp = await fetchApi<{ updateProduitCaracteristiques: boolean }>(
    mutation,
    {
      input: payload,
    },
  );

  return resp.updateProduitCaracteristiques;
}
export async function getProduitDescription(
  id_produit: number,
): Promise<ProduitDescription | null> {
  const query = `
    query ProduitDescription($id_produit: Int!) {
      produitDescription(id_produit: $id_produit) {
        id_produit
        texte_fr
        texte_uk
        texte_it
        texte_es
        texte_de
        texte_zh
        texte_de
        instructions
        commentaires
      }
    }
  `;

  const resp = await fetchApi<{
    produitDescription: ProduitDescription | null;
  }>(query, { id_produit });

  return resp.produitDescription;
}

export async function saveProduitDescription(
  payload: SaveProduitDescriptionPayload,
): Promise<boolean> {
  const mutation = `
    mutation SaveProduitDescription($input: SaveProduitDescriptionInput!) {
      saveProduitDescription(input: $input)
    }
  `;

  const resp = await fetchApi<{ saveProduitDescription: boolean }>(mutation, {
    input: payload,
  });

  return resp.saveProduitDescription;
}
// types
export interface SaveProduitStocksPayload {
  id_produit: number;
  items: { id_couleur: number; value: number; taille?: number | null }[];
}

// api
export async function saveProduitStocks(
  payload: SaveProduitStocksPayload,
): Promise<boolean> {
  const mutation = `
    mutation SaveProduitStocks($input: SaveProduitStocksInput!) {
      saveProduitStocks(input: $input)
    }
  `;

  const resp = await fetchApi<{ saveProduitStocks: boolean }>(mutation, {
    input: payload,
  });

  return resp.saveProduitStocks;
}

/**
 * Mettre à jour ou insérer un seul enregistrement de stock (upsert)
 * Utilisé pour mettre à jour le nombre de paquets (bundleCount) pour les produits couleur mélangée
 * ou pour mettre à jour le stock par taille pour les produits "à la taille"
 */
export async function upsertProduitStock(
  id_produit: number,
  id_couleur: number,
  value: number,
  taille?: string | null,
): Promise<boolean> {
  const mutation = `
    mutation UpsertProduitStock($id_produit: Int!, $id_couleur: Int!, $value: Int!, $taille: String) {
      upsertProduitStock(id_produit: $id_produit, id_couleur: $id_couleur, value: $value, taille: $taille)
    }
  `;

  const resp = await fetchApi<{ upsertProduitStock: boolean }>(mutation, {
    id_produit,
    id_couleur,
    value,
    taille: taille ?? null,
  });

  return resp.upsertProduitStock;
}

/**
 * Mettre à jour uniquement le description_paquet d'une couleur spécifique
 * Sans supprimer les autres enregistrements de couleurs ou de stock
 */
export async function updateCouleurDescriptionPaquet(
  id_produit: number,
  id_couleur: number,
  description_paquet: string,
): Promise<boolean> {
  const mutation = `
    mutation UpdateCouleurDescriptionPaquet($id_produit: Int!, $id_couleur: Int!, $description_paquet: String!) {
      updateCouleurDescriptionPaquet(id_produit: $id_produit, id_couleur: $id_couleur, description_paquet: $description_paquet)
    }
  `;

  const resp = await fetchApi<{ updateCouleurDescriptionPaquet: boolean }>(mutation, {
    id_produit,
    id_couleur,
    description_paquet,
  });

  return resp.updateCouleurDescriptionPaquet;
}

export async function softDeleteProduits(ids: number[]): Promise<boolean> {
  const normalizedIds = Array.from(
    new Set(
      ids
        .map(Number)
        .filter((id) => Number.isInteger(id) && id > 0),
    ),
  );
  // Aucune suppression ne doit partir avec une liste vide (notamment après
  // une double validation de la sélection dans la liste).
  if (normalizedIds.length === 0) return true;

  const mutation = `
    mutation SoftDeleteProduits($ids: [Int!]!) {
      softDeleteProduits(ids: $ids)
    }
  `;

  const resp = await fetchApi<{ softDeleteProduits: boolean }>(mutation, {
    ids: normalizedIds,
  });

  return resp.softDeleteProduits;
}

export async function softDeleteProduit(id: number): Promise<boolean> {
  return softDeleteProduits([id]);
}

export async function restoreProduit(
  id: number,
  referenceBase?: string,
): Promise<boolean> {
  const normalizedId = Number(id);
  if (!Number.isInteger(normalizedId) || normalizedId <= 0) return false;

  const mutation = `
    mutation RestoreProduit($id: Int!, $referenceBase: String) {
      restoreProduit(id: $id, referenceBase: $referenceBase)
    }
  `;

  const resp = await fetchApi<{ restoreProduit: boolean }>(mutation, {
    id: normalizedId,
    referenceBase: referenceBase?.trim() || null,
  });
  return resp.restoreProduit;
}

export async function restoreProduits(ids: number[]): Promise<boolean> {
  const mutation = `
    mutation RestoreProduits($ids: [Int!]!) {
      restoreProduits(ids: $ids)
    }
  `;
  const resp = await fetchApi<{ restoreProduits: boolean }>(mutation, { ids });
  return resp.restoreProduits;
}

// --- Bulk visibility (no admin needed) ---
export async function setProduitsVisible(
  ids: number[],
  visible: boolean,
): Promise<boolean> {
  const normalizedIds = Array.from(
    new Set(
      ids
        .map(Number)
        .filter((id) => Number.isInteger(id) && id > 0),
    ),
  );
  if (normalizedIds.length === 0) return true;

  const mutation = `
    mutation SetProduitsVisible($ids: [Int!]!, $visible: Boolean!) {
      setProduitsVisible(ids: $ids, visible: $visible)
    }
  `;
  const resp = await fetchApi<{ setProduitsVisible: boolean }>(mutation, {
    ids: normalizedIds,
    visible,
  });
  return resp.setProduitsVisible;
}

/**
 * Interface pour les couleurs d'un produit avec leurs quantités
 */
export interface ProduitCouleurAvecQuantites {
  id_couleur_produit: number;
  id_couleur: number | null;
  id_produit: number;
  description_paquet: string | null;
  couleur?: {
    id_couleur: number;
    couleur_FR: string;
    hex?: string | null;
  } | null;
}

/**
 * Récupérer les couleurs d'un produit avec leurs quantités (description_paquet)
 */
export async function getProduitCouleurs(
  id_produit: number,
): Promise<ProduitCouleurAvecQuantites[]> {
  const query = `
    query GetProduitCouleurs($id_produit: Int!) {
      couleursProduitByProduitId(id_produit: $id_produit) {
        id_couleur_produit
        id_couleur
        id_produit
        description_paquet
        couleur {
          id_couleur
          couleur_FR
          hex
        }
      }
    }
  `;
  const response = await fetchApi<{
    couleursProduitByProduitId: ProduitCouleurAvecQuantites[];
  }>(query, { id_produit });
  return response.couleursProduitByProduitId;
}

/**
 * Dupliquer un produit principal (main=1) avec une nouvelle couleur
 * Crée une variante (main=0) liée au produit principal
 */
export async function duplicateWithNewColor(
  idProduit: number,
  couleurId: number,
  couleurName: string,
): Promise<Produit> {
  const mutation = `
    mutation DuplicateWithNewColor($idProduit: Int!, $couleurId: Int!, $couleurName: String!) {
      duplicateWithNewColor(idProduit: $idProduit, couleurId: $couleurId, couleurName: $couleurName) {
        id_produit
        reference
        main
      }
    }
  `;
  const response = await fetchApi<{ duplicateWithNewColor: Produit }>(mutation, {
    idProduit,
    couleurId,
    couleurName,
  });
  return response.duplicateWithNewColor;
}

/**
 * Changer le produit principal dans un groupe de variantes
 * L'ancien main devient variante, le nouveau devient principal
 */
export async function toggleMainProduct(
  idProduit: number,
  applyOfflineRule = false,
): Promise<boolean> {
  const mutation = `
    mutation ToggleMainProduct($idProduit: Int!, $applyOfflineRule: Boolean) {
      toggleMainProduct(idProduit: $idProduit, applyOfflineRule: $applyOfflineRule)
    }
  `;
  const response = await fetchApi<{ toggleMainProduct: boolean }>(mutation, {
    idProduit,
    applyOfflineRule,
  });
  return response.toggleMainProduct;
}

/**
 * Récupère tous les IDs de couleur utilisés par un produit principal et ses variantes (tous statuts confondus)
 */
export async function getAllUsedColorIdsByMainProduct(
  mainId: number,
): Promise<number[]> {
  const query = `
    query GetAllUsedColorIdsByMainProduct($mainId: Int!) {
      allUsedColorIdsByMainProduct(mainId: $mainId)
    }
  `;
  const response = await fetchApi<{ allUsedColorIdsByMainProduct: number[] }>(
    query,
    { mainId },
  );
  return response.allUsedColorIdsByMainProduct;
}
